/**
 * Status de um nº de apólice no balde (`Balde de Apólices`, aba de Integrações).
 *
 * Os valores são exatamente como o BFF grava no DynamoDB — `Disponivel` SEM acento.
 * O acento existe só no rótulo. Use sempre este enum e os helpers abaixo, nunca um
 * mapa local, para que a tabela, os filtros e os modais de edição não divirjam.
 */
export enum PolicyBatchStatus {
  /** Livre para ser reservado pela próxima emissão do ramo. */
  Available = 'Disponivel',
  /** Reservado por uma emissão em andamento. */
  Reserved = 'Reservado',
  /** Já usado por uma apólice emitida. */
  Used = 'Utilizado',
}

export const POLICY_BATCH_STATUS_LABELS: Record<string, string> = {
  [PolicyBatchStatus.Available]: 'Disponível',
  [PolicyBatchStatus.Reserved]: 'Reservado',
  [PolicyBatchStatus.Used]: 'Utilizado',
};

/** `colorScheme` do `Tag` do design system por status. */
export const POLICY_BATCH_STATUS_COLORS: Record<
  string,
  'gray' | 'success' | 'yellow'
> = {
  [PolicyBatchStatus.Available]: 'success',
  [PolicyBatchStatus.Reserved]: 'yellow',
  [PolicyBatchStatus.Used]: 'gray',
};

/** Opções para os comboboxes de edição — sem a opção vazia. */
export const POLICY_BATCH_STATUS_OPTIONS = Object.values(PolicyBatchStatus).map(
  value => ({
    value,
    label: POLICY_BATCH_STATUS_LABELS[value],
  })
);

/** Opções para o filtro da listagem — com o "Todos" no topo. */
export const POLICY_BATCH_STATUS_FILTER_OPTIONS = [
  { label: 'Todos', value: '' },
  ...POLICY_BATCH_STATUS_OPTIONS,
];

/**
 * Resolve o rótulo de exibição de um status.
 * @param code valor vindo do BFF
 * @param fallback usado quando o código é vazio/desconhecido (default: o próprio código)
 */
export function getPolicyBatchStatusLabel(
  code?: string | null,
  fallback?: string
): string {
  const key = String(code ?? '').trim();
  if (!key) return fallback ?? '—';
  return POLICY_BATCH_STATUS_LABELS[key] ?? fallback ?? key;
}

export function getPolicyBatchStatusColor(
  code?: string | null
): 'gray' | 'success' | 'yellow' {
  return POLICY_BATCH_STATUS_COLORS[String(code ?? '').trim()] ?? 'gray';
}
